/**
 * Cart Context
 * Provides global cart state management
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import cartService from '../services/cartService';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const { user, isAuthenticated } = useAuth();

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = cartService.getCart();
    setCart(savedCart);
    updateCartCount(savedCart);
  }, []);

  // Merge cart when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      cartService.mergeWithBackend(user.id);
    }
  }, [isAuthenticated, user]);

  const updateCartCount = (items) => {
    const count = items.reduce((total, item) => total + item.quantity, 0);
    setCartCount(count);
  };

  const addItem = (product, quantity = 1, variation = null) => {
    const updatedCart = cartService.addItem(product, quantity, variation);
    setCart(updatedCart);
    updateCartCount(updatedCart);
  };

  const removeItem = (productId, variation = null) => {
    const updatedCart = cartService.removeItem(productId, variation);
    setCart(updatedCart);
    updateCartCount(updatedCart);
  };

  const updateQuantity = (productId, quantity, variation = null) => {
    const updatedCart = cartService.updateQuantity(productId, quantity, variation);
    setCart(updatedCart);
    updateCartCount(updatedCart);
  };

  const clearCart = () => {
    const updatedCart = cartService.clearCart();
    setCart(updatedCart);
    updateCartCount(updatedCart);
  };

  const getTotal = () => {
    return cartService.getTotal();
  };

  const value = {
    cart,
    cartCount,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getTotal
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

